// Escribir un PDF a mano — ADR-2026-09-08-el-agente-lee-y-crea-pdfs y
// ADR-2026-09-08-el-pdf-con-estilos-e-imagenes.
//
// Un PDF es un formato de OBJETOS en texto plano con una tabla de posiciones al final
// (el xref). Para lo que se pide desde el chat —un presupuesto, un catálogo, un informe—
// alcanza con las fuentes estándar, que todo lector trae y no hay que embeber.
//
// El modelo es el MISMO que usa Claude para sus archivos `.pdf`: `reportlab`, que es una
// librería de BLOQUES (párrafos, tablas, espaciadores) con estilos — no un conversor de
// HTML. La fidelidad tipo página web sólo se consigue imprimiendo con el motor del
// navegador, y eso cambia la descarga por un diálogo de impresión.
//
// Por eso acá no entra ni jsPDF ni pdf-lib: LEER un PDF sí necesita una librería de verdad
// (ver `leerPdf.ts`), pero escribir uno de texto, tablas e imágenes es acotado.
//
// Lo que NO hace: emojis y alfabetos no latinos (el texto va en WinAnsi, que cubre el
// español entero y nada más), ni layout libre: lo que hay son BLOQUES con forma —
// portada, bandas de sección, tarjetas en grilla, fichas con la foto al costado,
// cifras, etiquetas, dos columnas— además de los de texto, tabla e imagen
// (ADR-2026-09-12-el-pdf-gana-vocabulario-visual). Todo se dibuja con primitivas del
// PDF: rectángulos (redondeados con curvas de Bézier), recortes, texto e imágenes.

import { anchoDeTexto, partirEnLineas, recortarALoAncho } from './anchoTexto';

// ── Bloques que se pueden escribir ──────────────────────────────────────────

export type Alineacion = 'izquierda' | 'centro' | 'derecha';

export interface ImagenPdf {
  /** Bytes JPEG tal cual. Se embeben sin recodificar (`DCTDecode`). */
  jpeg: Uint8Array;
  ancho: number;
  alto: number;
}

/** Una tarjeta de la grilla `tarjetas`: foto arriba (opcional), cifra, título y texto. */
export interface TarjetaPdf {
  titulo?: string;
  texto?: string;
  /** Un número grande arriba del título (un precio, un porcentaje). */
  cifra?: string;
  /** Imagen del diccionario `imagenes`, recortada al ancho de la tarjeta. */
  ref?: string;
}

export interface CifraPdf {
  valor: string;
  etiqueta: string;
}

export type BloquePdf =
  | { tipo: 'titulo'; texto: string; align?: Alineacion; color?: string }
  | { tipo: 'subtitulo'; texto: string; align?: Alineacion; color?: string }
  | { tipo: 'parrafo'; texto: string; align?: Alineacion; color?: string }
  /** Texto chico y gris, en cursiva: una aclaración, una fuente, una fecha. */
  | { tipo: 'nota'; texto: string; align?: Alineacion }
  /** `items` de strings; con el nivel "Baja" pueden llegar como objetos — ver `textoDeItem`. */
  | { tipo: 'lista'; items: Array<string | Record<string, unknown>> }
  | {
      tipo: 'tabla';
      filas: string[][];
      /** `rayada`: filas alternadas con fondo suave. Default `lineas`. */
      estilo?: 'lineas' | 'rayada';
      /** `solido`: encabezado con fondo del acento y texto blanco. Default `suave`. */
      encabezado?: 'suave' | 'solido';
    }
  /** Caja de color con texto adentro: un total, una condición, una advertencia. */
  | { tipo: 'destacado'; texto: string; color?: string }
  /** Una imagen del diccionario `imagenes` del documento. `alto` en puntos (opcional). */
  | { tipo: 'imagen'; ref: string; alto?: number; align?: Alineacion }
  | { tipo: 'separador' }
  /** Página de apertura: banda del acento de borde a borde con el título en blanco,
   *  una bajada y una línea chica (fecha, cliente). Siempre arranca página. */
  | { tipo: 'portada'; texto: string; subtexto?: string; detalle?: string; color?: string }
  /** Encabezado de sección: barra del acento con el texto en blanco. */
  | { tipo: 'banda'; texto: string; color?: string }
  /** Grilla de tarjetas, 2 o 3 por fila, todas de la misma altura por fila. */
  | { tipo: 'tarjetas'; items: TarjetaPdf[]; columnas?: 2 | 3 }
  /** Fila de cifras grandes con su etiqueta (hasta 4 por fila). */
  | { tipo: 'cifras'; items: CifraPdf[] }
  /** Foto al costado (150pt) y, al lado, título, texto y una etiqueta (un precio). */
  | {
      tipo: 'ficha';
      ref?: string;
      titulo?: string;
      texto?: string;
      detalle?: string;
      lado?: 'izquierda' | 'derecha';
    }
  /** Dos columnas de texto, con un título opcional cada una. */
  | { tipo: 'columnas'; izquierda: string; derecha: string; titulos?: [string, string] }
  /** Pastillas: categorías, tags, atributos. */
  | { tipo: 'etiquetas'; items: Array<string | Record<string, unknown>>; color?: string };

export interface DocumentoPdf {
  titulo: string;
  bloques: BloquePdf[];
  /** Color de acento (hex `#RRGGBB`): títulos, bandas, tarjetas, encabezado de tabla. */
  color?: string;
  /** Las imágenes referenciadas por los bloques, ya decodificadas a JPEG. */
  imagenes?: Record<string, ImagenPdf>;
  /** Línea de pie, además del número de página. Ej. el nombre de la marca. */
  pie?: string;
  /** Línea chica arriba a la derecha de cada página salvo la primera. */
  encabezado?: string;
}

// ── Medidas de la hoja (A4 en puntos) ───────────────────────────────────────

const ANCHO = 595.28;
const ALTO = 841.89;
const MARGEN = 56; // ~2 cm
const ANCHO_UTIL = ANCHO - MARGEN * 2;
const ALTO_PIE = 28;
/** Alto de la banda de la portada. */
const ALTO_PORTADA = 220;

const ESTILOS = {
  titulo: { tamano: 20, alto: 26, negrita: true, antes: 0, despues: 10 },
  subtitulo: { tamano: 13, alto: 18, negrita: true, antes: 12, despues: 5 },
  parrafo: { tamano: 10.5, alto: 14.5, negrita: false, antes: 0, despues: 8 },
  nota: { tamano: 9, alto: 12.5, negrita: false, antes: 0, despues: 8 },
  lista: { tamano: 10.5, alto: 14.5, negrita: false, antes: 0, despues: 8 },
  tabla: { tamano: 9.5, alto: 16, negrita: false, antes: 4, despues: 10 },
  destacado: { tamano: 11, alto: 15, negrita: false, antes: 6, despues: 10 },
} as const;

const COLOR_POR_DEFECTO = '#1644EB'; // hex-de-marca-ok: default de un PDF generado, es contenido
/** El texto corriente no es negro puro: un azul muy oscuro se lee igual y no "quema". */
const TEXTO = '#1F2237';
const GRIS = '#6B6F80';
const GRIS_CLARO = '#8A8A93';

// ── Colores ─────────────────────────────────────────────────────────────────

/** `#RRGGBB` → los tres componentes 0..1 que el PDF espera. Lo ilegible cae a negro. */
export function colorPdf(hex: string | undefined): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const rg = (hex: string | undefined) =>
  colorPdf(hex)
    .map((c) => c.toFixed(3))
    .join(' ');

const aHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;

/** Mezcla un color con blanco. Para los fondos: el acento puro no deja leer. */
export function aclarar(hex: string | undefined, cuanto: number): string {
  const [r, g, b] = colorPdf(hex);
  const mezcla = (c: number) => (c + (1 - c) * cuanto) * 255;
  return aHex(mezcla(r), mezcla(g), mezcla(b));
}

/** Mezcla un color con negro. Para el texto sobre un fondo tenue del mismo acento. */
export function oscurecer(hex: string | undefined, cuanto: number): string {
  const [r, g, b] = colorPdf(hex);
  return aHex(r * (1 - cuanto) * 255, g * (1 - cuanto) * 255, b * (1 - cuanto) * 255);
}

// ── Codificación ────────────────────────────────────────────────────────────

/**
 * Texto → bytes WinAnsi (que es Latin-1 con unos cambios entre 0x80 y 0x9F).
 * Lo que no entra se reemplaza: mejor una interrogación que un PDF que no abre.
 */
const WINANSI_EXTRA: Record<string, number> = {
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87,
  'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a, '‹': 0x8b, 'Œ': 0x8c, 'Ž': 0x8e, '‘': 0x91,
  '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '˜': 0x98,
  '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c, 'ž': 0x9e, 'Ÿ': 0x9f,
};

export function aWinAnsi(texto: string): number[] {
  const out: number[] = [];
  for (const c of texto ?? '') {
    const extra = WINANSI_EXTRA[c];
    if (extra !== undefined) {
      out.push(extra);
      continue;
    }
    const cod = c.codePointAt(0) ?? 63;
    // Latin-1 entra tal cual; el resto (emojis, cirílico, chino) sale como "?".
    out.push(cod <= 0xff ? cod : 63);
  }
  return out;
}

/** Un string de PDF: entre paréntesis, con `(`, `)` y `\` escapados. */
export function stringPdf(texto: string): string {
  return `(${aWinAnsi(texto)
    .map((b) => {
      const c = String.fromCharCode(b);
      if (c === '(' || c === ')' || c === '\\') return `\\${c}`;
      // Los no imprimibles van en octal para no romper el stream.
      return b < 32 || b > 126 ? `\\${b.toString(8).padStart(3, '0')}` : c;
    })
    .join('')})`;
}

// ── Primitivas de dibujo ────────────────────────────────────────────────────

interface Pagina {
  ops: string[];
  /** Refs de imagen usadas: van en los `/Resources` de esa página. */
  imagenes: Set<string>;
}

/** F1 regular, F2 negrita, F3 cursiva (mide igual que la regular). */
const FUENTE = (negrita: boolean, cursiva = false) => (negrita ? '/F2' : cursiva ? '/F3' : '/F1');

const n2 = (v: number) => v.toFixed(2);

/** X de arranque según la alineación, dentro de una caja que empieza en `x0`. */
const xAlineado = (
  align: Alineacion | undefined,
  anchoTexto: number,
  anchoCaja: number,
  x0 = MARGEN,
) =>
  align === 'centro'
    ? x0 + (anchoCaja - anchoTexto) / 2
    : align === 'derecha'
      ? x0 + anchoCaja - anchoTexto
      : x0;

const escribirLinea = (
  x: number,
  y: number,
  texto: string,
  tamano: number,
  negrita: boolean,
  color?: string,
  cursiva = false,
) =>
  `BT ${color ? `${rg(color)} rg ` : ''}${FUENTE(negrita, cursiva)} ${tamano} Tf ` +
  `1 0 0 1 ${n2(x)} ${n2(y)} Tm ${stringPdf(texto)} Tj ET` +
  (color ? ` ${rg(TEXTO)} rg` : '');

/**
 * El camino de un rectángulo con esquinas redondeadas. Un PDF no tiene "rect redondeado":
 * se arma con cuatro curvas de Bézier (k ≈ 0.5523 es la constante que aproxima un cuarto
 * de círculo). Sirve para rellenar (`f`), trazar (`S`) y recortar (`W n`).
 */
export function caminoRedondeado(x: number, y: number, w: number, h: number, radio: number): string {
  const r = Math.max(0, Math.min(radio, w / 2, h / 2));
  const k = 0.5523 * r;
  return [
    `${n2(x + r)} ${n2(y)} m`,
    `${n2(x + w - r)} ${n2(y)} l`,
    `${n2(x + w - r + k)} ${n2(y)} ${n2(x + w)} ${n2(y + r - k)} ${n2(x + w)} ${n2(y + r)} c`,
    `${n2(x + w)} ${n2(y + h - r)} l`,
    `${n2(x + w)} ${n2(y + h - r + k)} ${n2(x + w - r + k)} ${n2(y + h)} ${n2(x + w - r)} ${n2(y + h)} c`,
    `${n2(x + r)} ${n2(y + h)} l`,
    `${n2(x + r - k)} ${n2(y + h)} ${n2(x)} ${n2(y + h - r + k)} ${n2(x)} ${n2(y + h - r)} c`,
    `${n2(x)} ${n2(y + r)} l`,
    `${n2(x)} ${n2(y + r - k)} ${n2(x + r - k)} ${n2(y)} ${n2(x + r)} ${n2(y)} c`,
    'h',
  ].join(' ');
}

const rellenar = (camino: string, color: string) => `${rg(color)} rg ${camino} f ${rg(TEXTO)} rg`;
const trazar = (camino: string, color: string, grosor = 0.6) =>
  `${rg(color)} RG ${n2(grosor)} w ${camino} S`;

/**
 * Dibuja una imagen CUBRIENDO una caja (como `object-fit: cover`): se escala hasta tapar
 * la caja entera y se recorta con el camino redondeado. Es lo que hace que una grilla de
 * fotos de distinta proporción quede pareja.
 */
const imagenCubriendo = (
  ref: string,
  img: ImagenPdf,
  x: number,
  y: number,
  w: number,
  h: number,
  radio: number,
) => {
  const escala = Math.max(w / Math.max(1, img.ancho), h / Math.max(1, img.alto));
  const dw = img.ancho * escala;
  const dh = img.alto * escala;
  const dx = x - (dw - w) / 2;
  const dy = y - (dh - h) / 2;
  return `q ${caminoRedondeado(x, y, w, h, radio)} W n ${n2(dw)} 0 0 ${n2(dh)} ${n2(dx)} ${n2(dy)} cm /I${ref} Do Q`;
};

/** Aire a los costados del texto dentro de una celda. */
const PADDING_CELDA = 8;

/** ¿Se lee como un número (precio, cantidad, porcentaje)? Esas celdas van a la derecha. */
/**
 * El texto de un ítem de `lista` o `etiquetas`, venga como string o como objeto.
 *
 * El schema de `crear_pdf` declara esos ítems como strings, pero el mismo campo `items`
 * es de objetos en `cifras` y `tarjetas`, así que el tipo real es una unión — y Gemini no
 * sabe expresarla: el traductor del backend la aplana a `object`
 * (ADR-2026-09-14-gemini-rechazaba-las-tools-del-chat). Con el nivel "Baja", entonces, una
 * lista llega como `[{texto: "…"}]`. Antes eso salía vacío (el filtro por `typeof`) y las
 * etiquetas directamente reventaban (`.trim()` sobre un objeto).
 *
 * Se lee la primera clave con contenido de las que el propio schema nombra. Ser tolerante
 * acá es más barato que pedirle a cada modelo que acierte la forma exacta.
 */
export function textoDeItem(item: unknown): string {
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number') return String(item);
  if (!item || typeof item !== 'object') return '';
  const o = item as Record<string, unknown>;
  for (const clave of ['texto', 'valor', 'titulo', 'etiqueta', 'cifra']) {
    const v = o[clave];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return '';
}

export const esNumerico = (celda: string): boolean =>
  /^[\s$€£%.,\-+\d]+$/.test(celda.trim()) && /\d/.test(celda);

/**
 * Anchos de columna: reparto proporcional al contenido, con un mínimo por columna.
 *
 * Dos cosas que parecen detalles y no lo son: el ancho natural incluye el PADDING (si no,
 * el texto entra justo y después el padding lo empuja afuera) y la primera fila se mide en
 * NEGRITA, que es como se dibuja. Sin lo segundo, "Cantidad" se medía en regular, entraba
 * justo, y salía impreso como "Cantid…".
 */
export function anchosDeColumna(filas: string[][], anchoTotal: number): number[] {
  const columnas = filas.reduce((n, f) => Math.max(n, f.length), 0);
  if (columnas === 0) return [];
  const naturales = Array.from({ length: columnas }, (_, c) =>
    Math.max(
      ...filas.map(
        (f, i) =>
          anchoDeTexto((f[c] ?? '').slice(0, 60), ESTILOS.tabla.tamano, i === 0) + PADDING_CELDA,
      ),
      20,
    ),
  );
  const suma = naturales.reduce((a, b) => a + b, 0);
  // Si entra natural, se respeta; si no, se escala. El mínimo evita que una columna con
  // celdas cortas quede tan angosta que no entre ni una palabra.
  const minimo = Math.min(45, anchoTotal / columnas);
  const escala = suma > anchoTotal ? anchoTotal / suma : 1;
  const escaladas = naturales.map((n) => Math.max(minimo, n * escala));
  const total = escaladas.reduce((a, b) => a + b, 0);
  return total > anchoTotal ? escaladas.map((n) => (n * anchoTotal) / total) : escaladas;
}

/** Alto al que se dibuja una imagen: respeta el pedido, la proporción y la caja. */
export function medidaDeImagen(
  img: ImagenPdf,
  altoPedido: number | undefined,
  anchoCaja: number,
  altoDisponible: number,
): { ancho: number; alto: number } {
  const proporcion = img.alto / Math.max(1, img.ancho);
  let ancho = Math.min(anchoCaja, img.ancho);
  let alto = ancho * proporcion;
  if (altoPedido && altoPedido > 0) {
    alto = altoPedido;
    ancho = alto / Math.max(0.0001, proporcion);
    if (ancho > anchoCaja) {
      ancho = anchoCaja;
      alto = ancho * proporcion;
    }
  }
  // Una imagen más alta que la hoja no se dibuja fuera: se achica.
  if (alto > altoDisponible) {
    alto = altoDisponible;
    ancho = alto / Math.max(0.0001, proporcion);
  }
  return { ancho, alto };
}

/** Un párrafo ya partido en líneas y acotado: lo que cada bloque compuesto necesita. */
const lineasDe = (texto: string | undefined, ancho: number, tamano: number, negrita: boolean, max: number) => {
  const lineas = partirEnLineas((texto ?? '').trim(), ancho, tamano, negrita);
  if (lineas.length <= max) return lineas;
  const cortadas = lineas.slice(0, max);
  cortadas[max - 1] = recortarALoAncho(`${cortadas[max - 1]}…`, ancho, tamano, negrita);
  return cortadas;
};

// ── Composición ─────────────────────────────────────────────────────────────

function componer(doc: DocumentoPdf): Pagina[] {
  const acento = doc.color || COLOR_POR_DEFECTO;
  const imagenes = doc.imagenes ?? {};
  const paginas: Pagina[] = [];
  let ops: string[] = [`${rg(TEXTO)} rg`];
  let usadas = new Set<string>();
  let y = ALTO - MARGEN;
  const nuevaPagina = () => {
    paginas.push({ ops, imagenes: usadas });
    ops = [`${rg(TEXTO)} rg`];
    usadas = new Set();
    y = ALTO - MARGEN;
  };
  const reservar = (alto: number) => {
    if (y - alto < MARGEN + ALTO_PIE) nuevaPagina();
  };
  const escribirParrafo = (
    x: number,
    yTope: number,
    lineas: string[],
    tamano: number,
    altoLinea: number,
    negrita: boolean,
    color?: string,
    cursiva = false,
  ): number => {
    let yy = yTope;
    for (const linea of lineas) {
      yy -= altoLinea;
      ops.push(escribirLinea(x, yy, linea, tamano, negrita, color, cursiva));
    }
    return lineas.length * altoLinea;
  };
  const conPortada = doc.bloques[0]?.tipo === 'portada';
  const bloques: BloquePdf[] = [
    // El título suelto va arriba de la primera página, salvo que haya portada: ahí el
    // título es el de la portada.
    ...(doc.titulo && !conPortada
      ? [{ tipo: 'titulo' as const, texto: doc.titulo, color: acento }]
      : []),
    ...doc.bloques,
  ];

  for (const bloque of bloques) {
    if (bloque.tipo === 'separador') {
      reservar(12);
      y -= 6;
      ops.push(`0.85 0.85 0.87 RG 0.7 w ${MARGEN} ${n2(y)} m ${n2(ANCHO - MARGEN)} ${n2(y)} l S`);
      y -= 8;
      continue;
    }

    if (bloque.tipo === 'portada') {
      // Siempre abre página: es la tapa de un documento o de un capítulo.
      if (ops.length > 1) nuevaPagina();
      const color = bloque.color || acento;
      ops.push(`${rg(color)} rg 0 ${n2(ALTO - ALTO_PORTADA)} ${n2(ANCHO)} ${n2(ALTO_PORTADA)} re f`);
      // Un segundo plano más claro, en diagonal chata: rompe el bloque de color sin
      // competir con el título.
      ops.push(
        `${rg(aclarar(color, 0.18))} rg ${n2(ANCHO * 0.62)} ${n2(ALTO - ALTO_PORTADA)} ${n2(ANCHO * 0.38)} ${n2(ALTO_PORTADA)} re f ${rg(TEXTO)} rg`,
      );
      const titulo = lineasDe(bloque.texto, ANCHO_UTIL, 26, true, 3);
      let yy = ALTO - 78;
      yy -= escribirParrafo(MARGEN, yy, titulo, 26, 32, true, '#FFFFFF');
      if (bloque.subtexto) {
        yy -= 6;
        yy -= escribirParrafo(MARGEN, yy, lineasDe(bloque.subtexto, ANCHO_UTIL, 12.5, false, 2), 12.5, 16.5, false, aclarar(color, 0.86));
      }
      if (bloque.detalle) {
        ops.push(escribirLinea(MARGEN, ALTO - ALTO_PORTADA + 18, recortarALoAncho(bloque.detalle, ANCHO_UTIL, 9, false), 9, false, aclarar(color, 0.8)));
      }
      y = ALTO - ALTO_PORTADA - 30;
      continue;
    }

    if (bloque.tipo === 'banda') {
      const h = 26;
      reservar(h + 22);
      y -= 8;
      const color = bloque.color || acento;
      ops.push(rellenar(caminoRedondeado(MARGEN, y - h, ANCHO_UTIL, h, 6), color));
      ops.push(escribirLinea(MARGEN + 10, y - h + 8.5, recortarALoAncho(bloque.texto, ANCHO_UTIL - 20, 12, true), 12, true, '#FFFFFF'));
      y -= h + 10;
      continue;
    }

    if (bloque.tipo === 'cifras') {
      const items = bloque.items.filter((c) => c && (c.valor || c.etiqueta));
      for (let desde = 0; desde < items.length; desde += 4) {
        const fila = items.slice(desde, desde + 4);
        const gap = 10;
        const w = (ANCHO_UTIL - gap * (fila.length - 1)) / fila.length;
        const h = 60;
        reservar(h + 12);
        fila.forEach((c, i) => {
          const x = MARGEN + i * (w + gap);
          ops.push(rellenar(caminoRedondeado(x, y - h, w, h, 8), aclarar(acento, 0.92)));
          ops.push(escribirLinea(x + 12, y - h + 30, recortarALoAncho(c.valor ?? '', w - 24, 20, true), 20, true, acento));
          ops.push(escribirLinea(x + 12, y - h + 14, recortarALoAncho(c.etiqueta ?? '', w - 24, 8.5, false), 8.5, false, GRIS));
        });
        y -= h + 12;
      }
      continue;
    }

    if (bloque.tipo === 'tarjetas') {
      const items = bloque.items.filter((t) => t && (t.titulo || t.texto || t.cifra || t.ref));
      const cols = bloque.columnas === 2 || bloque.columnas === 3 ? bloque.columnas : items.length <= 2 ? 2 : 3;
      const gap = 10;
      const pad = 10;
      const w = (ANCHO_UTIL - gap * (cols - 1)) / cols;
      const anchoTexto = w - pad * 2;
      for (let desde = 0; desde < items.length; desde += cols) {
        const fila = items.slice(desde, desde + cols);
        const conFoto = fila.some((t) => t.ref && imagenes[t.ref]);
        const altoFoto = conFoto ? Math.round(w * 0.72) : 0;
        const medidas = fila.map((t) => {
          const titulo = lineasDe(t.titulo, anchoTexto, 10.5, true, 2);
          const texto = lineasDe(t.texto, anchoTexto, 9.5, false, 6);
          const alto =
            pad +
            (altoFoto ? altoFoto + 8 : 0) +
            (t.cifra ? 24 : 0) +
            titulo.length * 14 +
            (texto.length ? texto.length * 13 + (titulo.length ? 4 : 0) : 0) +
            pad;
          return { titulo, texto, alto };
        });
        const h = Math.max(...medidas.map((m) => m.alto));
        reservar(h + 12);
        fila.forEach((t, i) => {
          const x = MARGEN + i * (w + gap);
          const camino = caminoRedondeado(x, y - h, w, h, 10);
          ops.push(rellenar(camino, aclarar(acento, 0.94)));
          ops.push(trazar(camino, aclarar(acento, 0.78)));
          let yy = y;
          if (altoFoto) {
            const img = t.ref ? imagenes[t.ref] : undefined;
            if (img && t.ref) {
              ops.push(`q ${camino} W n ${imagenCubriendo(t.ref, img, x, y - altoFoto, w, altoFoto, 0)} Q`);
              usadas.add(t.ref);
            }
            yy -= altoFoto + 8;
          }
          yy -= pad;
          if (t.cifra) {
            ops.push(escribirLinea(x + pad, yy - 18, recortarALoAncho(t.cifra, anchoTexto, 18, true), 18, true, acento));
            yy -= 24;
          }
          yy -= escribirParrafo(x + pad, yy, medidas[i].titulo, 10.5, 14, true, oscurecer(acento, 0.35));
          if (medidas[i].texto.length) {
            if (medidas[i].titulo.length) yy -= 4;
            escribirParrafo(x + pad, yy, medidas[i].texto, 9.5, 13, false, TEXTO);
          }
        });
        y -= h + 12;
      }
      continue;
    }

    if (bloque.tipo === 'ficha') {
      const img = bloque.ref ? imagenes[bloque.ref] : undefined;
      const lado = bloque.lado === 'derecha' ? 'derecha' : 'izquierda';
      const fotoW = img ? 150 : 0;
      const fotoH = img ? 150 : 0;
      const anchoTexto = ANCHO_UTIL - (img ? fotoW + 16 : 0);
      const xTexto = MARGEN + (img && lado === 'izquierda' ? fotoW + 16 : 0);
      const titulo = lineasDe(bloque.titulo, anchoTexto, 12.5, true, 2);
      const texto = lineasDe(bloque.texto, anchoTexto, 10, false, 8);
      const detalle = (bloque.detalle ?? '').trim();
      const altoTexto =
        titulo.length * 16 + (texto.length ? texto.length * 14 + (titulo.length ? 4 : 0) : 0) + (detalle ? 28 : 0);
      const h = Math.max(fotoH, altoTexto + 4);
      reservar(h + 14);
      if (img && bloque.ref) {
        const xFoto = lado === 'izquierda' ? MARGEN : MARGEN + ANCHO_UTIL - fotoW;
        ops.push(imagenCubriendo(bloque.ref, img, xFoto, y - fotoH, fotoW, fotoH, 10));
        usadas.add(bloque.ref);
      }
      let yy = y - 2;
      yy -= escribirParrafo(xTexto, yy, titulo, 12.5, 16, true, acento);
      if (texto.length) {
        if (titulo.length) yy -= 4;
        yy -= escribirParrafo(xTexto, yy, texto, 10, 14, false, TEXTO);
      }
      if (detalle) {
        const t = recortarALoAncho(detalle, anchoTexto - 16, 9, true);
        const wPill = anchoDeTexto(t, 9, true) + 16;
        yy -= 26;
        ops.push(rellenar(caminoRedondeado(xTexto, yy, wPill, 18, 9), acento));
        ops.push(escribirLinea(xTexto + 8, yy + 5.5, t, 9, true, '#FFFFFF'));
      }
      y -= h + 14;
      continue;
    }

    if (bloque.tipo === 'columnas') {
      const gap = 20;
      const w = (ANCHO_UTIL - gap) / 2;
      const lados = [
        { titulo: bloque.titulos?.[0], texto: bloque.izquierda },
        { titulo: bloque.titulos?.[1], texto: bloque.derecha },
      ].map((l) => ({
        titulo: lineasDe(l.titulo, w, 11, true, 2),
        texto: lineasDe(l.texto, w, 10, false, 40),
      }));
      const alto = (l: (typeof lados)[number]) => l.titulo.length * 15 + (l.titulo.length ? 4 : 0) + l.texto.length * 14;
      const h = Math.max(...lados.map(alto));
      reservar(h + 10);
      lados.forEach((l, i) => {
        const x = MARGEN + i * (w + gap);
        let yy = y;
        yy -= escribirParrafo(x, yy, l.titulo, 11, 15, true, acento);
        if (l.titulo.length) yy -= 4;
        escribirParrafo(x, yy, l.texto, 10, 14, false, TEXTO);
      });
      y -= h + 10;
      continue;
    }

    if (bloque.tipo === 'etiquetas') {
      const color = bloque.color || acento;
      const h = 18;
      const padX = 8;
      const gap = 6;
      let x = MARGEN;
      let abierta = false;
      for (const item of bloque.items) {
        const t = recortarALoAncho(textoDeItem(item), ANCHO_UTIL - padX * 2, 8.5, true);
        if (!t) continue;
        const w = anchoDeTexto(t, 8.5, true) + padX * 2;
        if (!abierta || x + w > MARGEN + ANCHO_UTIL) {
          if (abierta) y -= h + gap;
          reservar(h + gap);
          x = MARGEN;
          abierta = true;
        }
        ops.push(rellenar(caminoRedondeado(x, y - h, w, h, 9), aclarar(color, 0.88)));
        ops.push(escribirLinea(x + padX, y - h + 5.5, t, 8.5, true, oscurecer(color, 0.2)));
        x += w + gap;
      }
      if (abierta) y -= h + 10;
      continue;
    }

    if (bloque.tipo === 'imagen') {
      const img = imagenes[bloque.ref];
      if (!img) continue;
      const { ancho, alto } = medidaDeImagen(img, bloque.alto, ANCHO_UTIL, ALTO - MARGEN * 2 - ALTO_PIE);
      reservar(alto + 10);
      const x = xAlineado(bloque.align ?? 'centro', ancho, ANCHO_UTIL);
      y -= alto;
      // `cm` posiciona y escala; el XObject se dibuja en el cuadrado unidad.
      ops.push(`q ${n2(ancho)} 0 0 ${n2(alto)} ${n2(x)} ${n2(y)} cm /I${bloque.ref} Do Q`);
      usadas.add(bloque.ref);
      y -= 10;
      continue;
    }

    if (bloque.tipo === 'destacado') {
      const est = ESTILOS.destacado;
      const color = bloque.color || acento;
      const lineas = partirEnLineas(bloque.texto ?? '', ANCHO_UTIL - 28, est.tamano);
      const altoCaja = lineas.length * est.alto + 14;
      reservar(altoCaja + est.antes);
      y -= est.antes;
      ops.push(rellenar(caminoRedondeado(MARGEN, y - altoCaja, ANCHO_UTIL, altoCaja, 8), aclarar(color, 0.9)));
      // Barra de acento a la izquierda: es lo que hace que se lea como un destacado y no
      // como un párrafo con fondo.
      ops.push(rellenar(caminoRedondeado(MARGEN, y - altoCaja, 4, altoCaja, 2), color));
      let yTexto = y - 12;
      for (const linea of lineas) {
        ops.push(escribirLinea(MARGEN + 16, yTexto, linea, est.tamano, false, oscurecer(color, 0.45)));
        yTexto -= est.alto;
      }
      y -= altoCaja + est.despues;
      continue;
    }

    if (bloque.tipo === 'tabla') {
      const est = ESTILOS.tabla;
      const filas = bloque.filas.filter((f) => Array.isArray(f));
      if (filas.length === 0) continue;
      const anchos = anchosDeColumna(filas, ANCHO_UTIL);
      const rayada = bloque.estilo === 'rayada';
      const solido = bloque.encabezado === 'solido';
      // Una columna es numérica si todas sus celdas del cuerpo (las que tienen algo) lo
      // son: ahí los números se alinean a la derecha, que es como se comparan.
      const numerica = anchos.map((_, c) => {
        const celdas = filas.slice(1).map((f) => f[c] ?? '').filter((t) => t.trim());
        return celdas.length > 0 && celdas.every(esNumerico);
      });
      y -= est.antes;
      filas.forEach((fila, i) => {
        reservar(est.alto);
        const esEncabezado = i === 0;
        if (esEncabezado) {
          ops.push(
            solido
              ? rellenar(caminoRedondeado(MARGEN, y - est.alto + 4, ANCHO_UTIL, est.alto, 4), acento)
              : `${rg(aclarar(acento, 0.88))} rg ${MARGEN} ${n2(y - est.alto + 4)} ${n2(ANCHO_UTIL)} ${n2(est.alto)} re f ${rg(TEXTO)} rg`,
          );
        } else if (rayada && i % 2 === 0) {
          ops.push(
            `${rg(aclarar(acento, 0.96))} rg ${MARGEN} ${n2(y - est.alto + 4)} ${n2(ANCHO_UTIL)} ${n2(est.alto)} re f ${rg(TEXTO)} rg`,
          );
        }
        let x = MARGEN;
        anchos.forEach((ancho, c) => {
          const celda = recortarALoAncho(fila[c] ?? '', ancho - PADDING_CELDA, est.tamano, esEncabezado);
          const aDerecha = numerica[c];
          const xCelda = aDerecha
            ? x + ancho - PADDING_CELDA / 2 - anchoDeTexto(celda, est.tamano, esEncabezado)
            : x + PADDING_CELDA / 2;
          ops.push(
            escribirLinea(
              xCelda,
              y - est.alto + 9,
              celda,
              est.tamano,
              esEncabezado,
              esEncabezado ? (solido ? '#FFFFFF' : acento) : undefined,
            ),
          );
          x += ancho;
        });
        y -= est.alto;
        if (!esEncabezado && !rayada && i < filas.length - 1) {
          ops.push(`0.9 0.9 0.92 RG 0.5 w ${MARGEN} ${n2(y)} m ${n2(ANCHO - MARGEN)} ${n2(y)} l S`);
        }
      });
      y -= est.despues;
      continue;
    }

    if (bloque.tipo === 'nota') {
      const est = ESTILOS.nota;
      const lineas = partirEnLineas(bloque.texto ?? '', ANCHO_UTIL, est.tamano);
      for (const linea of lineas) {
        reservar(est.alto);
        y -= est.alto;
        const x = bloque.align && bloque.align !== 'izquierda'
          ? xAlineado(bloque.align, anchoDeTexto(linea, est.tamano), ANCHO_UTIL)
          : MARGEN;
        ops.push(escribirLinea(x, y, linea, est.tamano, false, GRIS, true));
      }
      y -= est.despues;
      continue;
    }

    // titulo / subtitulo / parrafo / lista
    const est =
      bloque.tipo === 'titulo'
        ? ESTILOS.titulo
        : bloque.tipo === 'subtitulo'
          ? ESTILOS.subtitulo
          : bloque.tipo === 'lista'
            ? ESTILOS.lista
            : ESTILOS.parrafo;
    y -= est.antes;
    const trozos =
      bloque.tipo === 'lista' ? bloque.items.map(textoDeItem).filter(Boolean) : [bloque.texto ?? ''];
    const align = bloque.tipo === 'lista' ? undefined : bloque.align;
    const color =
      bloque.tipo === 'lista'
        ? undefined
        : bloque.color || (bloque.tipo === 'subtitulo' ? acento : bloque.tipo === 'titulo' ? oscurecer(acento, 0.2) : undefined);
    for (const trozo of trozos) {
      const sangria = bloque.tipo === 'lista' ? 16 : 0;
      const lineas = partirEnLineas(trozo, ANCHO_UTIL - sangria, est.tamano, est.negrita);
      lineas.forEach((linea, i) => {
        reservar(est.alto);
        y -= est.alto;
        if (bloque.tipo === 'lista' && i === 0) {
          ops.push(escribirLinea(MARGEN, y, '•', est.tamano, false, acento));
        }
        const x =
          align && align !== 'izquierda'
            ? xAlineado(align, anchoDeTexto(linea, est.tamano, est.negrita), ANCHO_UTIL)
            : MARGEN + sangria;
        ops.push(escribirLinea(x, y, linea, est.tamano, est.negrita, color));
      });
    }
    y -= est.despues;
  }
  paginas.push({ ops, imagenes: usadas });

  // El pie y el encabezado van al final, cuando ya se sabe cuántas páginas hay.
  paginas.forEach((pagina, i) => {
    const izq = doc.pie ?? '';
    const der = `${i + 1} / ${paginas.length}`;
    const yPie = MARGEN - 14;
    if (izq) pagina.ops.push(escribirLinea(MARGEN, yPie, izq, 8.5, false, GRIS_CLARO));
    pagina.ops.push(escribirLinea(MARGEN + ANCHO_UTIL - anchoDeTexto(der, 8.5), yPie, der, 8.5, false, GRIS_CLARO));
    if (doc.encabezado && i > 0) {
      const t = recortarALoAncho(doc.encabezado, ANCHO_UTIL, 8.5, false);
      pagina.ops.push(escribirLinea(MARGEN + ANCHO_UTIL - anchoDeTexto(t, 8.5), ALTO - MARGEN + 22, t, 8.5, false, GRIS_CLARO));
    }
  });
  return paginas;
}

// ── Serialización ───────────────────────────────────────────────────────────

/** Un objeto del PDF: su diccionario y, si es un stream, los bytes crudos. */
interface ObjetoPdf {
  texto: string;
  binario?: Uint8Array;
}

/**
 * Documento → bytes de un PDF 1.4.
 *
 * El `xref` guarda el OFFSET EN BYTES de cada objeto. Se cuenta sobre bytes y no sobre
 * caracteres a propósito: con un acento en el título —o con una imagen embebida— un offset
 * contado en caracteres queda corrido y el PDF no abre.
 */
export function escribirPdf(doc: DocumentoPdf): Uint8Array {
  const paginas = componer(doc);
  const imagenes = doc.imagenes ?? {};
  const objetos: ObjetoPdf[] = [];
  const agregar = (texto: string, binario?: Uint8Array): number => {
    objetos.push(binario ? { texto, binario } : { texto });
    return objetos.length; // 1-based
  };

  const idCatalogo = agregar('');
  const idPaginas = agregar('');
  const idF1 = agregar(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
  );
  const idF2 = agregar(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
  );
  // La cursiva mide igual que la regular: no necesita métricas propias.
  const idF3 = agregar(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>',
  );

  // Las imágenes son XObjects: los bytes JPEG van tal cual con `DCTDecode`, sin
  // recodificar. Es lo que hace que embeber una foto sea barato.
  const idImagen = new Map<string, number>();
  for (const [ref, img] of Object.entries(imagenes)) {
    idImagen.set(
      ref,
      agregar(
        `<< /Type /XObject /Subtype /Image /Width ${img.ancho} /Height ${img.alto} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.jpeg.length} >>\nstream\n`,
        img.jpeg,
      ),
    );
  }

  const idsPagina: number[] = [];
  for (const pagina of paginas) {
    const contenido = pagina.ops.join('\n');
    const idContenido = agregar(
      `<< /Length ${aWinAnsi(contenido).length} >>\nstream\n${contenido}\nendstream`,
    );
    const xobjects = [...pagina.imagenes]
      .filter((ref) => idImagen.has(ref))
      .map((ref) => `/I${ref} ${idImagen.get(ref)} 0 R`)
      .join(' ');
    idsPagina.push(
      agregar(
        `<< /Type /Page /Parent ${idPaginas} 0 R /MediaBox [0 0 ${ANCHO} ${ALTO}] ` +
          `/Resources << /Font << /F1 ${idF1} 0 R /F2 ${idF2} 0 R /F3 ${idF3} 0 R >>` +
          (xobjects ? ` /XObject << ${xobjects} >>` : '') +
          ` >> /Contents ${idContenido} 0 R >>`,
      ),
    );
  }

  objetos[idCatalogo - 1] = { texto: `<< /Type /Catalog /Pages ${idPaginas} 0 R >>` };
  objetos[idPaginas - 1] = {
    texto: `<< /Type /Pages /Kids [${idsPagina.map((i) => `${i} 0 R`).join(' ')}] /Count ${idsPagina.length} >>`,
  };

  const bytes: number[] = [];
  const escribir = (s: string) => {
    for (const b of aWinAnsi(s)) bytes.push(b);
  };
  escribir('%PDF-1.4\n');
  // Comentario binario: le avisa a los lectores que el archivo NO es sólo texto. Sin esto
  // algunos transportes lo tratan como texto y corrompen los bytes de las imágenes.
  bytes.push(0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a);
  const offsets: number[] = [];
  objetos.forEach((obj, i) => {
    offsets.push(bytes.length);
    escribir(`${i + 1} 0 obj\n${obj.texto}`);
    if (obj.binario) {
      for (const b of obj.binario) bytes.push(b);
      escribir('\nendstream');
    }
    escribir('\nendobj\n');
  });
  const inicioXref = bytes.length;
  escribir(`xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`);
  for (const off of offsets) escribir(`${String(off).padStart(10, '0')} 00000 n \n`);
  escribir(
    `trailer\n<< /Size ${objetos.length + 1} /Root ${idCatalogo} 0 R >>\nstartxref\n${inicioXref}\n%%EOF\n`,
  );
  return new Uint8Array(bytes);
}
